import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft,
  DollarSign,
  Users,
  Calendar,
  Edit,
  Phone,
  Mail,
  FileText,
  Wallet,
  CreditCard,
  UserCheck,
  ShoppingBag,
  GitBranch,
  Download,
  Building2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Landmark,
  Hash,
  Shield,
} from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
  paid: 'bg-green-100 text-green-700',
  submitted: 'bg-blue-100 text-blue-600',
  approved: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-gray-100 text-gray-500',
  confirmed: 'bg-blue-100 text-blue-700',
  spa_signed: 'bg-purple-100 text-purple-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
};

const AdminCustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [walletData, setWalletData] = useState({ wallet: null, transactions: [] });
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [hierarchy, setHierarchy] = useState({ upline_chain: [], downlines: [] });
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const res = await api.get(`/customers/${id}`);
      setCustomer(res.data);
      setEditForm({
        full_name: res.data.full_name || '',
        phone: res.data.phone || '',
        ic_number: res.data.ic_number || '',
        bank_name: res.data.bank_name || '',
        bank_holder_name: res.data.bank_holder_name || '',
        bank_account: res.data.bank_account || '',
        bank_account_number: res.data.bank_account_number || '',
        address: res.data.address || '',
      });

      // Load all tab data in parallel
      const results = await Promise.allSettled([
        api.get(`/customers/${id}/bookings`),
        api.get(`/customers/${id}/appointments`),
        api.get(`/customers/${id}/wallet`),
        api.get(`/customers/${id}/referrals`),
        api.get(`/customers/${id}/payouts`),
        api.get(`/customers/${id}/claims`),
        api.get(`/customers/${id}/hierarchy`),
      ]);

      if (results[0].status === 'fulfilled') setBookings(results[0].value.data);
      if (results[1].status === 'fulfilled') setAppointments(results[1].value.data);
      if (results[2].status === 'fulfilled') setWalletData(results[2].value.data);
      if (results[3].status === 'fulfilled') setReferrals(results[3].value.data);
      if (results[4].status === 'fulfilled') setPayouts(results[4].value.data);
      if (results[5].status === 'fulfilled') setClaims(results[5].value.data);
      if (results[6].status === 'fulfilled') setHierarchy(results[6].value.data);
    } catch (error) {
      console.error('Error loading customer:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/customers/${id}`, editForm);
      toast.success('Customer updated');
      setShowEditSheet(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const handleDownloadClaim = (claim) => {
    const content = [
      'CLAIM STATEMENT',
      '================',
      `Claim ID: ${claim.id}`,
      `Date: ${claim.created_at?.split('T')[0] || '-'}`,
      `Status: ${claim.status}`,
      `Type: ${claim.claim_type || '-'}`,
      `Commission Amount: RM ${(claim.agent_commission || 0).toFixed(2)}`,
      `Property: ${claim.property_name || claim.property_id || '-'}`,
      `Booking ID: ${claim.booking_id || '-'}`,
      '',
      `Customer: ${customer?.full_name}`,
      `Customer No: ${customer?.customer_number || '-'}`,
      `IC Number: ${customer?.ic_number || '-'}`,
      '',
      'Generated on: ' + new Date().toLocaleDateString(),
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claim_statement_${claim.id}.txt`;
    a.click();
    toast.success('Claim statement downloaded');
  };

  const handleDownloadAllClaims = () => {
    const lines = [
      'CLAIM STATEMENTS SUMMARY',
      `Customer: ${customer?.full_name} (${customer?.customer_number || '-'})`,
      `Generated: ${new Date().toLocaleDateString()}`,
      '='.repeat(60),
      '',
    ];

    claims.forEach((claim, idx) => {
      lines.push(`${idx + 1}. Claim ID: ${claim.id}`);
      lines.push(`   Date: ${claim.created_at?.split('T')[0] || '-'}`);
      lines.push(`   Status: ${claim.status}`);
      lines.push(`   Amount: RM ${(claim.agent_commission || 0).toFixed(2)}`);
      lines.push(`   Booking: ${claim.booking_id || '-'}`);
      lines.push('');
    });

    lines.push(`Total Claims: ${claims.length}`);
    lines.push(`Total Amount: RM ${claims.reduce((s, c) => s + (c.agent_commission || 0), 0).toFixed(2)}`);

    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_claims_${customer?.customer_number || id}.txt`;
    a.click();
    toast.success('All claim statements downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-2 border-[#4E50FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Customer not found</p>
        <Button variant="outline" onClick={() => navigate('/admin/customers')} className="mt-4">Back to Customers</Button>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[customer.status || 'active'] || 'bg-gray-100 text-gray-500';
  const kycColor = STATUS_COLORS[customer.kyc_status || 'pending'] || 'bg-yellow-100 text-yellow-700';

  return (
    <div className="p-6 space-y-6" data-testid="customer-details-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/customers')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">Customer Details</h1>
              <Badge className={statusColor}>{customer.status || 'active'}</Badge>
            </div>
            <p className="text-sm text-gray-500">{customer.customer_number || 'No customer number'}</p>
          </div>
        </div>
        <Button className="bg-[#4E50FF] hover:bg-[#3E40DD]" onClick={() => setShowEditSheet(true)} data-testid="edit-customer-btn">
          <Edit className="w-4 h-4 mr-2" />
          Edit Customer
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Profile Card */}
        <div className="col-span-3">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-[#4E50FF] to-[#7B61FF] p-6 text-center text-white">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3 text-3xl font-bold">
                {customer.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <h2 className="text-lg font-bold">{customer.full_name}</h2>
              <p className="text-white/80 text-sm">{customer.email}</p>
              <Badge className="bg-white/20 text-white mt-2">{customer.customer_number || 'Customer'}</Badge>
            </div>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{customer.phone || '-'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700 truncate">{customer.email}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">IC: {customer.ic_number || '-'}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="flex items-center gap-1">
                  KYC: <Badge className={`${kycColor} text-xs`}>{customer.kyc_status || 'pending'}</Badge>
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">Joined: {formatDate(customer.created_at)}</span>
              </div>
              <hr className="my-2" />
              <div className="flex items-center gap-3 text-sm">
                <Landmark className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-gray-700 font-medium">{customer.bank_name || 'No bank info'}</p>
                  <p className="text-gray-500 text-xs">{customer.bank_holder_name || '-'}</p>
                  <p className="text-gray-500 text-xs">{customer.bank_account || customer.bank_account_number || '-'}</p>
                </div>
              </div>
              {customer.referral_code && (
                <div className="flex items-center gap-3 text-sm">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700 font-mono">{customer.referral_code}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="mt-4 space-y-2">
            {[
              { label: 'Wallet Balance', value: formatCurrency(customer.wallet_balance || 0), icon: Wallet, color: 'text-green-600' },
              { label: 'Commission Earned', value: formatCurrency(customer.total_commission_earned || 0), icon: DollarSign, color: 'text-blue-600' },
              { label: 'Total Bookings', value: customer.total_bookings || 0, icon: ShoppingBag, color: 'text-purple-600' },
              { label: 'Total Referrals', value: customer.total_referrals || 0, icon: Users, color: 'text-amber-600' },
              { label: 'Total Payouts', value: formatCurrency(customer.total_payouts || 0), icon: CreditCard, color: 'text-red-600' },
            ].map(stat => (
              <Card key={stat.label}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    <span className="text-xs text-gray-500">{stat.label}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900">{stat.value}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-9">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100/80 p-1 rounded-lg mb-4">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
              <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments ({appointments.length})</TabsTrigger>
              <TabsTrigger value="wallet" data-testid="tab-wallet">Wallet</TabsTrigger>
              <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals ({referrals.length})</TabsTrigger>
              <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts ({payouts.length})</TabsTrigger>
              <TabsTrigger value="claims" data-testid="tab-claims">Claims ({claims.length})</TabsTrigger>
              <TabsTrigger value="hierarchy" data-testid="tab-hierarchy">Hierarchy</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-base">Personal Information</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: 'Full Name', value: customer.full_name },
                      { label: 'Email', value: customer.email },
                      { label: 'Phone', value: customer.phone || '-' },
                      { label: 'Customer Number', value: customer.customer_number || '-' },
                      { label: 'IC / ID Number', value: customer.ic_number || '-' },
                      { label: 'Address', value: customer.address || '-' },
                    ].map(item => (
                      <div key={item.label} className="flex justify-between text-sm">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="text-gray-900 font-medium text-right max-w-[60%] truncate">{item.value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Banking Details</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: 'Bank Name', value: customer.bank_name || '-' },
                      { label: 'Account Name', value: customer.bank_holder_name || '-' },
                      { label: 'Account Number', value: customer.bank_account || customer.bank_account_number || '-' },
                    ].map(item => (
                      <div key={item.label} className="flex justify-between text-sm">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="text-gray-900 font-medium">{item.value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Recent Bookings</CardTitle></CardHeader>
                  <CardContent>
                    {bookings.length === 0 ? (
                      <p className="text-sm text-gray-400">No bookings yet</p>
                    ) : (
                      <div className="space-y-2">
                        {bookings.slice(0, 3).map(b => (
                          <div key={b.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{b.property_name || 'Property'}</p>
                              <p className="text-xs text-gray-500">{b.unit_number || ''}</p>
                            </div>
                            <Badge className={STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-500'}>
                              {b.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-base">Recent Appointments</CardTitle></CardHeader>
                  <CardContent>
                    {appointments.length === 0 ? (
                      <p className="text-sm text-gray-400">No appointments yet</p>
                    ) : (
                      <div className="space-y-2">
                        {appointments.slice(0, 3).map(a => (
                          <div key={a.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{a.property_name || 'Property'}</p>
                              <p className="text-xs text-gray-500">{a.preferred_date} {a.preferred_time}</p>
                            </div>
                            <Badge className={STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-500'}>
                              {a.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Booking Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Buyer</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No bookings</TableCell></TableRow>
                      ) : bookings.map(b => (
                        <TableRow key={b.id}>
                          <TableCell className="font-medium text-sm">{b.property_name || '-'}</TableCell>
                          <TableCell className="text-sm">{b.unit_number || '-'}</TableCell>
                          <TableCell className="text-sm">{b.buyer_name || '-'}</TableCell>
                          <TableCell className="text-sm">{formatCurrency(b.unit_price || b.deposit_amount || 0)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[b.status] || 'bg-gray-100'}>{b.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{formatDate(b.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Appointments Tab */}
            <TabsContent value="appointments">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Appointment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Agent</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No appointments</TableCell></TableRow>
                      ) : appointments.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium text-sm">{a.property_name || '-'}</TableCell>
                          <TableCell className="text-sm">{a.agent_name || '-'}</TableCell>
                          <TableCell className="text-sm">{a.preferred_date || '-'}</TableCell>
                          <TableCell className="text-sm">{a.preferred_time || '-'}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[a.status] || 'bg-gray-100'}>{a.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{a.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Wallet Tab */}
            <TabsContent value="wallet">
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-500">Current Balance</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(walletData.wallet?.balance || customer.wallet_balance || 0)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-500">Total Credits</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {formatCurrency(walletData.transactions?.filter(t => t.type === 'credit').reduce((s, t) => s + (t.amount || 0), 0) || 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-500">Total Debits</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency(walletData.transactions?.filter(t => t.type === 'debit').reduce((s, t) => s + (t.amount || 0), 0) || 0)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                <Card>
                  <CardHeader><CardTitle className="text-base">Transaction History</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(walletData.transactions || []).length === 0 ? (
                          <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No transactions</TableCell></TableRow>
                        ) : walletData.transactions.map((t, i) => (
                          <TableRow key={t.id || i}>
                            <TableCell className="text-sm">{formatDate(t.created_at)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {t.type === 'credit' ? (
                                  <ArrowDownRight className="w-4 h-4 text-green-500" />
                                ) : (
                                  <ArrowUpRight className="w-4 h-4 text-red-500" />
                                )}
                                <span className={`text-sm font-medium ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                  {t.type}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{t.description || '-'}</TableCell>
                            <TableCell className={`text-sm font-medium ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount || 0)}
                            </TableCell>
                            <TableCell className="text-sm">{formatCurrency(t.balance_after || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Referrals Tab */}
            <TabsContent value="referrals">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Referral Activity</CardTitle>
                    <Badge variant="outline">{referrals.length} referrals</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referrals.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-400">No referrals</TableCell></TableRow>
                      ) : referrals.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium text-sm">{r.full_name}</TableCell>
                          <TableCell className="text-sm">{r.email}</TableCell>
                          <TableCell className="text-sm capitalize">{r.role}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[r.status || 'active'] || 'bg-gray-100'}>{r.status || 'active'}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{formatDate(r.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              <Card>
                <CardHeader><CardTitle className="text-base">Payout Records</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payouts.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No payouts</TableCell></TableRow>
                      ) : payouts.map(p => (
                        <TableRow key={p.id}>
                          <TableCell className="text-sm">{formatDate(p.created_at)}</TableCell>
                          <TableCell className="text-sm font-medium">{formatCurrency(p.amount || 0)}</TableCell>
                          <TableCell className="text-sm">{p.bank_name || '-'}</TableCell>
                          <TableCell className="text-sm">{p.bank_account || '-'}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[p.status] || 'bg-gray-100'}>{p.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{p.reference || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Claims Tab */}
            <TabsContent value="claims">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Claim Statements</CardTitle>
                    {claims.length > 0 && (
                      <Button size="sm" variant="outline" onClick={handleDownloadAllClaims} data-testid="download-all-claims">
                        <Download className="w-4 h-4 mr-2" />
                        Download All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Claim ID</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Download</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {claims.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No claims</TableCell></TableRow>
                      ) : claims.map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm font-mono">{c.id?.slice(0, 8)}...</TableCell>
                          <TableCell className="text-sm capitalize">{c.claim_type || '-'}</TableCell>
                          <TableCell className="text-sm font-medium">{formatCurrency(c.agent_commission || 0)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[c.status] || 'bg-gray-100'}>{c.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{formatDate(c.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => handleDownloadClaim(c)} data-testid={`download-claim-${c.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Hierarchy Tab */}
            <TabsContent value="hierarchy">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-base">Upline Chain</CardTitle></CardHeader>
                  <CardContent>
                    {hierarchy.upline_chain.length === 0 ? (
                      <p className="text-sm text-gray-400">No upline</p>
                    ) : (
                      <div className="space-y-2">
                        {hierarchy.upline_chain.map((u, i) => (
                          <div key={u.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-[#4E50FF]/10 flex items-center justify-center text-[#4E50FF] font-bold text-sm">
                              {i + 1}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{u.full_name}</p>
                              <p className="text-xs text-gray-500 capitalize">{u.role} {u.agent_code ? `(${u.agent_code})` : u.customer_number ? `(${u.customer_number})` : ''}</p>
                            </div>
                            <GitBranch className="w-4 h-4 text-gray-300 ml-auto" />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Downlines</CardTitle>
                      <Badge variant="outline">{hierarchy.downlines.length}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {hierarchy.downlines.length === 0 ? (
                      <p className="text-sm text-gray-400">No downlines</p>
                    ) : (
                      <div className="space-y-2">
                        {hierarchy.downlines.map(d => (
                          <div key={d.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium">{d.full_name}</p>
                              <p className="text-xs text-gray-500 capitalize">{d.role} {d.customer_number ? `(${d.customer_number})` : ''}</p>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(d.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent className="w-[450px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Customer</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            {[
              { key: 'full_name', label: 'Full Name' },
              { key: 'phone', label: 'Phone' },
              { key: 'ic_number', label: 'IC / Identification Card Number' },
              { key: 'address', label: 'Address' },
              { key: 'bank_name', label: 'Bank Name' },
              { key: 'bank_holder_name', label: 'Bank Account Name' },
              { key: 'bank_account', label: 'Bank Account Number' },
            ].map(field => (
              <div key={field.key}>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{field.label}</label>
                <Input
                  value={editForm[field.key] || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  data-testid={`edit-${field.key}`}
                />
              </div>
            ))}
            <div className="flex gap-3 pt-4">
              <Button className="flex-1 bg-[#4E50FF] hover:bg-[#3E40DD]" onClick={handleSaveEdit} data-testid="save-customer-btn">
                Save Changes
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowEditSheet(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminCustomerDetails;
